"use client"
import { useQuery } from "@blitzjs/rpc"
import { useAtom, useSetAtom } from "jotai"
import { useEffect, useRef } from "react"

import {
  useMap,
  LayersConfig,
  useMapImage,
  useMapTooltip,
  useMapZoomDisclosure,
  useMapFeatureEvents,
  useMapHoverFeatureState,
} from "react-hooks"
import { MAP_LAYERS, DashboardSourceNames, TES_BLOCKGROUP_FACET } from "../dashboard.constants"
import { clickedFeatureAtom, mapAtom } from "../dashboard.state"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import { Blockgroup } from "db"
import getExtent from "app/blockgroups/queries/getExtent"
import { TesLayer } from "tes-types"
import { getFilterForLayer } from "../utils"
import { getTileServerUrlFormatter } from "app/utils/map_utils"
import { MapPopUp } from "./map-pop-up"
import { BLOCKGROUP_ZOOM, MUNICIPALITY_ZOOM } from "app/constants"
import { useTranslation } from "react-i18next"

export const Map = ({ blockgroups }: { blockgroups: Blockgroup[] }) => {
  const mapDiv = useRef<HTMLDivElement>(null)
  const [bounds] = useQuery(getExtent, {
    blockgroupIds: blockgroups.map((bg) => bg.id),
  })
  const [mapOnState, setMapOnState] = useAtom(mapAtom)
  const setClickedFeature = useSetAtom(clickedFeatureAtom)
  const mapLayersWithFilters = MAP_LAYERS.map((layer) => {
    return {
      ...layer,
      filter: getFilterForLayer(layer.source, blockgroups),
    } as TesLayer
  })

  const layersConfig: LayersConfig = {
    tileServerFormatter: getTileServerUrlFormatter("national"),
    layers: mapLayersWithFilters,
    sourcesZoom: {
      national_blockgroups: { minZoom: 10, maxZoom: 11 },
      national_municipalities: { minZoom: 2, maxZoom: 8 },
    },
  }

  const { map } = useMap({
    mapDiv: mapDiv,
    mapboxglAccessToken: process.env.NEXT_PUBLIC_MAPBOXGL_ACCESS_TOKEN!,
    layersConfig,
    options: {
      hash: false,
      bounds: bounds as [number, number, number, number],
      padding: 3,
      navigationControl: true,
      dragRotate: false,
    },
  })

  const { clickedFeature, clickedFeatureEventId, hoveredFeature } = useMapFeatureEvents({
    map,
    layers: mapLayersWithFilters,
  })
  useMapHoverFeatureState({ map, feature: hoveredFeature })

  useEffect(() => {
    if (map && !mapOnState) {
      setMapOnState(map as mapboxgl.Map)
      // Avoid map scroll zooming
      map.scrollZoom.disable()
    }
  }, [map, mapOnState])

  // clickedFeatureEventId ensures re-clicks on the same feature re-fires the effect, providing feature toggle behavior
  useEffect(() => {
    setClickedFeature(clickedFeature as MapboxGeoJSONFeature | null)
  }, [clickedFeature, clickedFeatureEventId])

  useEffect(() => {
    return () => setClickedFeature(null)
  }, [])

  const { mapImage } = useMapImage({
    mapRef: mapDiv,
    mapLoaded: Boolean(map),
  })
  const { t } = useTranslation(["facets", "map"])
  const tesLabel = t("facets:tree_equity_score.name")
  const compositeLabel = t("map:composite_score_tooltip_name")

  useMapTooltip({
    map,
    activeFacet: TES_BLOCKGROUP_FACET,
    layers: MAP_LAYERS.map((layer) => layer.id),
    getHoverPopupContent: (feature) => MapPopUp({ feature, tesLabel, compositeLabel }),
  })

  useMapZoomDisclosure({
    map,
    zoomLayersSteps: [
      {
        id: DashboardSourceNames.AGGREGATED_MUNICIPALITY,
        nextStep: MUNICIPALITY_ZOOM,
      },
      { id: DashboardSourceNames.MUNICIPALITY, nextStep: BLOCKGROUP_ZOOM },
    ],
  })

  return (
    <>
      <div className="-mb-16 border-t border-gray-200 bg-[#F9FCFC] pb-10 pt-5">
        <header className="flex w-full flex-col pt-8 text-center">
          <h1 className="text-base font-extrabold uppercase text-brand-green-darker">
            {t("location-insights:compare.title")}
          </h1>
          <h2 className="mt-3 pb-4 text-base font-medium tracking-wide text-gray-700">
            {t("location-insights:compare.subtitle")}
          </h2>
        </header>
        <div
          className="mapboxgl-map m-auto h-[46vh] w-full rounded-t-xl border-x border-t border-gray-300 sm:flex-auto xl:w-11/12 2xl:w-4/5 print:hidden"
          ref={mapDiv}
        />
        {mapImage && (
          <img
            src={mapImage}
            className="hidden print:mt-4 print:block print:h-[500px] print:w-full"
          />
        )}
      </div>
    </>
  )
}
